import { useState, useEffect } from 'react';
import { Car, Save, Eye, EyeOff, CheckCircle, XCircle, RefreshCw, ExternalLink, Info, Search } from 'lucide-react';
import { adminApi } from '../../services/api';

interface OemSettingsData {
  clientId: string;
  environment: 'sandbox' | 'production';
  enabled: boolean;
  hasClientSecret: boolean;
}

/**
 * Brands covered by our High Mobility application (mirrors the HM production console's
 * brand list). Values are the exact HM slugs. A brand still only works if HM has enabled
 * it for the app — this list reflects exactly that. `sandbox` is the emulated-vehicle
 * brand and only works in the sandbox environment.
 */
const HM_BRANDS: Array<{ value: string; label: string }> = [
  { value: 'bmw', label: 'BMW' },
  { value: 'mini', label: 'MINI' },
  { value: 'mercedes-benz', label: 'Mercedes-Benz' },
  { value: 'kia', label: 'Kia' },
  { value: 'toyota', label: 'Toyota' },
  { value: 'lexus', label: 'Lexus' },
  { value: 'renault', label: 'Renault' },
  { value: 'dacia', label: 'Dacia' },
  { value: 'ford', label: 'Ford' },
  { value: 'porsche', label: 'Porsche' },
  { value: 'peugeot', label: 'Peugeot' },
  { value: 'citroen', label: 'Citroën' },
  { value: 'opel', label: 'Opel' },
  { value: 'vauxhall', label: 'Vauxhall' },
  { value: 'ds', label: 'DS Automobiles' },
  { value: 'fiat', label: 'Fiat' },
  { value: 'alfaromeo', label: 'Alfa Romeo' },
  { value: 'jeep', label: 'Jeep' },
  { value: 'maserati', label: 'Maserati' },
  { value: 'tesla', label: 'Tesla' },
  { value: 'cupra', label: 'Cupra' },
  { value: 'volkswagen', label: 'Volkswagen' },
  { value: 'seat', label: 'SEAT' },
  { value: 'skoda', label: 'Škoda' },
  { value: 'audi', label: 'Audi' },
  { value: 'volvo-cars', label: 'Volvo' },
  { value: 'polestar', label: 'Polestar' },
  { value: 'nissan', label: 'Nissan' },
  { value: 'hyundai', label: 'Hyundai' },
  { value: 'sandbox', label: 'Sandbox (véhicule émulé)' },
];

/**
 * Super-admin platform settings for High Mobility (OEM Connect). The client_id /
 * client_secret of the TrackFleet High Mobility app are stored encrypted in the
 * DB (not .env) and used for the fleet client_credentials flow.
 */
export default function OemSettingsPage() {
  const [settings, setSettings] = useState<OemSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    clientId: '',
    clientSecret: '',
    environment: 'sandbox' as 'sandbox' | 'production',
    enabled: true,
  });

  // Pre-sale VIN eligibility test
  const [eligForm, setEligForm] = useState({ vin: '', brand: '' });
  const [eligLoading, setEligLoading] = useState(false);
  const [eligError, setEligError] = useState('');
  const [eligResult, setEligResult] = useState<{
    vin: string;
    eligible: boolean;
    connectivityStatus?: string;
    dataDelivery?: string[];
    primaryUserAssigned?: boolean;
  } | null>(null);

  const handleEligibility = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eligForm.vin.trim() || !eligForm.brand) return;
    setEligLoading(true);
    setEligResult(null);
    setEligError('');
    try {
      const res = await adminApi.checkOemEligibility(eligForm.vin.trim(), eligForm.brand);
      setEligResult(res.data);
    } catch (err: unknown) {
      const e2 = err as { response?: { data?: { error?: string } } };
      setEligError(e2.response?.data?.error || 'Échec de la vérification');
    } finally {
      setEligLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await adminApi.getOemSettings();
      if (res.data.settings) {
        setSettings(res.data.settings);
        setFormData({
          clientId: res.data.settings.clientId || '',
          clientSecret: '',
          environment: res.data.settings.environment || 'sandbox',
          enabled: res.data.settings.enabled,
        });
      }
    } catch (error) {
      console.error('Erreur chargement paramètres High Mobility:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveMessage(null);
    try {
      await adminApi.updateOemSettings({
        clientId: formData.clientId,
        clientSecret: formData.clientSecret || undefined,
        environment: formData.environment,
        enabled: formData.enabled,
      });
      setSaveMessage({ type: 'success', text: 'Identifiants High Mobility sauvegardés' });
      setFormData((prev) => ({ ...prev, clientSecret: '' }));
      loadSettings();
    } catch (error: unknown) {
      const e = error as { response?: { data?: { error?: string } } };
      setSaveMessage({ type: 'error', text: e.response?.data?.error || 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Données constructeur — High Mobility</h1>
        <p className="text-gray-500">
          Identifiants de l'application High Mobility (module OEM Connect). Stockés chiffrés en base.
        </p>
      </div>

      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            Ces identifiants (Client ID / Client Secret) sont ceux de votre application Fleet dans la{' '}
            <a href="https://www.high-mobility.com" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline inline-flex items-center gap-1">
              console High Mobility <ExternalLink className="h-3 w-3" />
            </a>
            {' '}(section API Credentials / OAuth Client). Ils authentifient TrackFleet auprès de High Mobility (flux <code className="bg-white px-1 rounded">client_credentials</code>).
          </p>
        </div>
      </div>

      {/* Statut */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Statut</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            {settings?.clientId ? <CheckCircle className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-red-400" />}
            <div>
              <p className="text-sm font-medium text-gray-900">Client ID</p>
              <p className="text-xs text-gray-500">{settings?.clientId ? 'Configuré' : 'Non configuré'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {settings?.hasClientSecret ? <CheckCircle className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-red-400" />}
            <div>
              <p className="text-sm font-medium text-gray-900">Client Secret</p>
              <p className="text-xs text-gray-500">{settings?.hasClientSecret ? 'Configuré' : 'Non configuré'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CheckCircle className={`h-5 w-5 ${settings?.enabled !== false ? 'text-green-500' : 'text-gray-300'}`} />
            <div>
              <p className="text-sm font-medium text-gray-900">Environnement</p>
              <p className="text-xs text-gray-500">{settings?.environment ?? 'sandbox'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <form onSubmit={handleSave} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <Car className="h-6 w-6 text-primary-500" />
          <h2 className="text-lg font-semibold text-gray-900">Identifiants OAuth</h2>
        </div>

        <div>
          <label className="block text-gray-700 text-sm font-medium mb-1">Client ID</label>
          <input
            type="text"
            value={formData.clientId}
            onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
            className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            placeholder="0e3547d0-ab04-..."
          />
        </div>

        <div>
          <label className="block text-gray-700 text-sm font-medium mb-1">Client Secret</label>
          <div className="relative">
            <input
              type={showSecret ? 'text' : 'password'}
              value={formData.clientSecret}
              onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
              className="w-full px-4 py-2 pr-10 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
              placeholder={settings?.hasClientSecret ? '••••••••••••••••••••••••' : 'client secret'}
            />
            <button
              type="button"
              onClick={() => setShowSecret(!showSecret)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">Laissez vide pour conserver le secret existant.</p>
        </div>

        <div>
          <label className="block text-gray-700 text-sm font-medium mb-1">Environnement</label>
          <select
            value={formData.environment}
            onChange={(e) => setFormData({ ...formData, environment: e.target.value as 'sandbox' | 'production' })}
            className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          >
            <option value="sandbox">Sandbox (test)</option>
            <option value="production">Production</option>
          </select>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="oemEnabled"
            checked={formData.enabled}
            onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="oemEnabled" className="text-gray-700">Activer l'intégration High Mobility</label>
        </div>

        {saveMessage && (
          <div
            className={`p-3 rounded-lg text-sm ${
              saveMessage.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {saveMessage.text}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Sauvegarder
          </button>
        </div>
      </form>

      {/* Testeur de VIN (avant vente) */}
      <form onSubmit={handleEligibility} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Search className="h-6 w-6 text-primary-500" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Tester un VIN (avant vente)</h2>
            <p className="text-sm text-gray-500">Vérifiez qu'un véhicule peut remonter des données avant de vendre l'option au client.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input
            type="text"
            value={eligForm.vin}
            onChange={(e) => setEligForm({ ...eligForm, vin: e.target.value })}
            className="sm:col-span-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            placeholder="VIN (17 caractères)"
          />
          <select
            value={eligForm.brand}
            onChange={(e) => setEligForm({ ...eligForm, brand: e.target.value })}
            required
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          >
            <option value="" disabled>Marque…</option>
            {HM_BRANDS.map((b) => (
              <option key={b.value} value={b.value}>{b.label}</option>
            ))}
          </select>
        </div>

        <button type="submit" disabled={eligLoading || !eligForm.vin.trim() || !eligForm.brand} className="btn-secondary flex items-center gap-2">
          {eligLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Tester l'éligibilité
        </button>

        {eligError && (
          <div className="p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-700 flex items-center gap-2">
            <XCircle className="h-4 w-4 flex-shrink-0" />
            {eligError}
          </div>
        )}

        {eligResult && (
          <div className={`p-4 rounded-lg text-sm border ${eligResult.eligible ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-center gap-2 font-medium mb-2">
              {eligResult.eligible ? (
                <><CheckCircle className="h-4 w-4 text-green-600" /><span className="text-green-700">Véhicule éligible</span></>
              ) : (
                <><XCircle className="h-4 w-4 text-amber-600" /><span className="text-amber-700">Non éligible</span></>
              )}
            </div>
            <ul className="text-gray-600 space-y-0.5">
              <li>VIN : <span className="font-mono">{eligResult.vin}</span></li>
              {eligResult.connectivityStatus && <li>Connectivité : {eligResult.connectivityStatus}</li>}
              {eligResult.dataDelivery && eligResult.dataDelivery.length > 0 && <li>Livraison des données : {eligResult.dataDelivery.join(', ')}</li>}
              {eligResult.primaryUserAssigned !== undefined && <li>Utilisateur principal assigné : {eligResult.primaryUserAssigned ? 'oui' : 'non'}</li>}
            </ul>
          </div>
        )}
      </form>
    </div>
  );
}
